/**
 * HUD role-context hook + per-card visibility helper.
 *
 * Combines:
 *   1. Relay org membership role (owner / admin / member) from OrganizationContext
 *   2. Forge user_roles grants (admin, super_admin, finance, finance_admin,
 *      bookkeeper, ar_clerk, ap_clerk, etc. — see public.app_role enum)
 *   3. is_staff_member(user_id) RPC result (used by Forge projects/tasks RLS)
 *
 * v1 hard-codes per-role visibility defaults in HUD_CARD_VISIBILITY (defined
 * by each card module). User-customizable layout is deferred — when it lands,
 * this becomes a default that can be overridden by per-user config.
 *
 * NOTE on the finance gate: the SELECT policy on finance_ar_invoices uses
 * `can_access_finance_org(org_id)` with the 1-arg default, which only
 * allows ['admin', 'super_admin', 'platform_super_admin', 'platform_support',
 * 'platform_readonly']. So `requiresAnyForgeRole` for finance cards must
 * match that set — a bookkeeper-only user will currently NOT see AR/AP
 * cards (their RLS gate blocks the underlying query). Widening this is a
 * Forge-side policy change; out of HUD v1 scope.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";

/** Role/permission context for the current authenticated user. */
export interface HudRoleContext {
  userId: string | null;
  /** Relay org membership role. Null when no active org. */
  relayRole: "owner" | "admin" | "member" | null;
  /** Forge `app_role` grants for the active org. Empty array when none. */
  forgeRoles: string[];
  /** Result of public.is_staff_member(user_id). False when no user. */
  isStaff: boolean;
  /** True while any underlying query is loading. */
  isLoading: boolean;
}

/** Visibility predicate declared by each HUD card. */
export interface HudCardVisibility {
  /** Card is visible to users whose Relay role is in this set. */
  relayRoles?: Array<"owner" | "admin" | "member">;
  /** Additionally, user must have ANY of these Forge roles. */
  requiresAnyForgeRole?: string[];
  /** Additionally, public.is_staff_member() must return true. */
  requiresStaff?: boolean;
}

/**
 * Pure visibility check — no React, easy to test.
 *
 * Each predicate field is treated as "absent OR empty array = no constraint".
 * That keeps the two array-typed predicates (relayRoles, requiresAnyForgeRole)
 * symmetrical: passing `[]` does NOT silently hide from everyone, which would
 * be a footgun for a future caller. If a card wants to be hidden from
 * everyone, it should not be rendered at all by its parent — not gated via
 * an empty-array predicate here.
 */
export function isCardVisible(
  viz: HudCardVisibility,
  ctx: HudRoleContext,
): boolean {
  if (
    viz.relayRoles &&
    viz.relayRoles.length > 0 &&
    (!ctx.relayRole || !viz.relayRoles.includes(ctx.relayRole))
  ) {
    return false;
  }
  if (viz.requiresAnyForgeRole && viz.requiresAnyForgeRole.length > 0) {
    const hasAny = viz.requiresAnyForgeRole.some((r) => ctx.forgeRoles.includes(r));
    if (!hasAny) return false;
  }
  if (viz.requiresStaff && !ctx.isStaff) return false;
  return true;
}

/**
 * Fetches Forge role grants + is_staff_member result for the current user.
 * Combines with OrganizationContext to produce the full HudRoleContext.
 */
export function useCurrentUserRoles(): HudRoleContext {
  const { user } = useAuth();
  const {
    organizationId,
    organizationRole,
    isLoading: orgLoading,
  } = useOrganization();

  // Forge roles + is_staff_member live in the public schema. Single query
  // returns both so we don't waste a round-trip.
  const query = useQuery({
    queryKey: ["hud-role-context", user?.id, organizationId],
    enabled: !!user?.id && !!organizationId,
    staleTime: 5 * 60 * 1000, // role grants don't change frequently
    queryFn: async () => {
      if (!user?.id || !organizationId) {
        return { forgeRoles: [] as string[], isStaff: false };
      }
      const publicClient = supabase.schema("public");

      const [rolesResult, staffResult] = await Promise.all([
        publicClient
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .or(`organization_id.eq.${organizationId},organization_id.is.null`),
        publicClient.rpc("is_staff_member", { _user_id: user.id }),
      ]);

      if (rolesResult.error) throw rolesResult.error;
      if (staffResult.error) throw staffResult.error;

      // The Database generic types `role` as the app_role enum (a string
      // union). We widen to plain string for the public HudRoleContext
      // shape so consumers don't have to import the enum.
      const forgeRoles = Array.from(
        new Set((rolesResult.data ?? []).map((r) => r.role as string)),
      );

      return {
        forgeRoles,
        isStaff: staffResult.data === true,
      };
    },
  });

  return {
    userId: user?.id ?? null,
    relayRole: organizationRole,
    forgeRoles: query.data?.forgeRoles ?? [],
    isStaff: query.data?.isStaff ?? false,
    // OR with orgLoading so the initial page-load window (auth resolved,
    // org membership query still in flight) is correctly seen as loading.
    // Without this, cards that branch on `!isLoading && forgeRoles.length === 0`
    // would briefly render an "unauthorized" state before the org loads.
    isLoading: orgLoading || query.isPending,
  };
}

/** Convenience role-set constants used by card visibility declarations. */
export const ROLES = {
  ALL_RELAY: ["owner", "admin", "member"] as const,
  OWNER_ADMIN: ["owner", "admin"] as const,
  /**
   * Forge roles that pass the 1-arg `can_access_finance_org()` SELECT gate.
   * Use for any card that reads `public.finance_*` tables.
   */
  FINANCE_READ: [
    "admin",
    "super_admin",
    "platform_super_admin",
    "platform_support",
    "platform_readonly",
  ],
} as const;
